import { useState, useEffect } from 'react'
import { type MRT_PaginationState } from 'material-react-table'
import * as Sentry from '@sentry/react'
import { getDeniedSubmissions, deletePendingSubmission, restoreDeniedSubmission, searchDeniedSubmissions } from '../../../pocketbase/PocketbasePendingSubmissionsService'
import { usePbMetadataStore } from '../../../stores/pbMetadataStore'
import { pb } from '../../../pocketbase/Pocketbase'
import type { INote, IPerfumer } from '../../../pocketbase/PocketbaseMetadataService'
import { DeniedSubmissionsTable } from './DeniedSubmissionsTable'
import type { IPendingSubmissionWithMetadata } from '../submissions/SubmissionsView'
import { ConfirmationDialog } from '../../../components/dialogs/ConfirmationDialog'

export const DeniedSubmissionsView = () => {
  const [submissions, setSubmissions] = useState<any[]>([])
  const [submissionsWithMetadata, setSubmissionsWithMetadata] = useState<IPendingSubmissionWithMetadata[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)
  const [searchValue, setSearchValue] = useState('')

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null)

  const { getBrandById, getNoteById, getPerfumerById, getCountryById, getPriceById, fetchAllMetadata, isLoaded: isMetadataLoaded } = usePbMetadataStore()

  // Fetch metadata on mount
  useEffect(() => {
    fetchAllMetadata()
  }, [])

  // Reset to first page when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchValue])

  // Load submissions when pagination or search changes
  useEffect(() => {
    loadSubmissions()
  }, [pagination.pageIndex, pagination.pageSize, searchValue])

  // Convert submissions to metadata format whenever submissions or metadata changes
  useEffect(() => {
    const withMetadata = submissions.map(submission => {
      return convertToMetadata(submission)
    }).filter(Boolean) as IPendingSubmissionWithMetadata[]

    setSubmissionsWithMetadata(withMetadata)
  }, [submissions, isMetadataLoaded])

  const convertToMetadata = (submission: any): IPendingSubmissionWithMetadata => {
    // Get image URL if image exists
    let imgUrl: string | null = null
    if (submission.image) {
      imgUrl = pb.files.getURL(submission, submission.image)
    }

    // Parse JSON arrays for custom notes
    const parseJsonArray = (value: string[] | string | null | undefined): string[] => {
      if (!value) return []

      // If it's already an array, return it
      if (Array.isArray(value)) {
        return value
      }

      // If it's a string, try to parse it
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        console.error('Failed to parse JSON array', e)
        Sentry.captureException(e)
        return []
      }
    }

    return {
      id: submission.id,
      name: submission.name,
      imgUrl,
      description: submission.description || '',
      year: submission.year || null,

      // Brand - check if relation exists, otherwise use text
      brand: submission.brand ? getBrandById(submission.brand) : undefined,
      brand_text: submission.brand_text || undefined,

      // Country - check if relation exists, otherwise use text
      country: submission.country ? getCountryById(submission.country) : undefined,
      country_text: submission.country_text || undefined,

      // Perfumers - combine relations and text
      perfumers: submission.perfumers
        ? (Array.isArray(submission.perfumers)
          ? submission.perfumers.map((id: string) => getPerfumerById(id)).filter(Boolean) as IPerfumer[]
          : [])
        : [],
      perfumers_text: parseJsonArray(submission.perfumers_text),

      // Price - always a relation
      price: submission.price ? getPriceById(submission.price) : undefined,

      // Top Notes - combine relations and text
      topNotes: submission.top_notes
        ? (Array.isArray(submission.top_notes)
          ? submission.top_notes.map((id: string) => getNoteById(id)).filter(Boolean) as INote[]
          : [])
        : [],
      topNotes_text: parseJsonArray(submission.top_notes_text),

      // Middle Notes - combine relations and text
      middleNotes: submission.middle_notes
        ? (Array.isArray(submission.middle_notes)
          ? submission.middle_notes.map((id: string) => getNoteById(id)).filter(Boolean) as INote[]
          : [])
        : [],
      middleNotes_text: parseJsonArray(submission.middle_notes_text),

      // Bottom Notes - combine relations and text
      bottomNotes: submission.bottom_notes
        ? (Array.isArray(submission.bottom_notes)
          ? submission.bottom_notes.map((id: string) => getNoteById(id)).filter(Boolean) as INote[]
          : [])
        : [],
      bottomNotes_text: parseJsonArray(submission.bottom_notes_text),

      // Denial fields
      is_denied: submission.is_denied || false,
      denied_reason: submission.denied_reason || '',

      // Metadata
      submitted_by: submission.submitted_by,
      created: submission.created,
    }
  }

  const loadSubmissions = async () => {
    try {
      setIsLoading(true)

      // Pocketbase uses 1-indexed pages
      const page = pagination.pageIndex + 1
      const search = searchValue ? searchValue.trim() : undefined

      const result = search
        ? await searchDeniedSubmissions(page, pagination.pageSize, search)
        : await getDeniedSubmissions(page, pagination.pageSize)

      setSubmissions(result.items)
      setRowCount(result.totalItems)
    } catch (error) {
      console.error('Error loading denied submissions:', error)
      Sentry.captureException(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = (submissionId: string) => {
    setSelectedSubmissionId(submissionId)
    setDeleteDialogOpen(true)
  }

  const handleRestore = (submissionId: string) => {
    setSelectedSubmissionId(submissionId)
    setRestoreDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedSubmissionId) return

    try {
      await deletePendingSubmission(selectedSubmissionId)
      setDeleteDialogOpen(false)
      setSelectedSubmissionId(null)
      loadSubmissions()
    } catch (error) {
      console.error('Error deleting submission:', error)
      Sentry.captureException(error)
      alert('Failed to delete submission. Please try again.')
    }
  }

  const confirmRestore = async () => {
    if (!selectedSubmissionId) return

    try {
      await restoreDeniedSubmission(selectedSubmissionId)
      setRestoreDialogOpen(false)
      setSelectedSubmissionId(null)
      loadSubmissions()
    } catch (error) {
      console.error('Error restoring submission:', error)
      Sentry.captureException(error)
      alert('Failed to restore submission. Please try again.')
    }
  }

  return (
    <div className={'flex flex-col h-full'}>
      {/* Fixed Header */}
      <div
        className={
          'flex items-center justify-between px-8 pb-6 pt-8 border-b border-searchBorder'
        }
      >
        <h1 className={'text-2xl font-bold'}>
          Denied Submissions
        </h1>
      </div>

      {/* Material React Table */}
      <div className={'flex-1 overflow-hidden'}>
        <DeniedSubmissionsTable
          data={submissionsWithMetadata}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onDelete={handleDelete}
          onRestore={handleRestore}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Submission"
        content="Are you sure you want to permanently delete this denied submission? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Restore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        onConfirm={confirmRestore}
        title="Restore Submission"
        content="Are you sure you want to restore this submission? It will be moved back to pending submissions for review."
        confirmLabel="Restore"
        cancelLabel="Cancel"
      />
    </div>
  )
}
